import { createGlobalStyle } from 'styled-components';
import theme from 'styled-theming';

// Define theme interface
declare module 'styled-components' {
  export interface DefaultTheme {
    theme: 'light' | 'dark';
  }
}

// Global styles using styled-theming for dynamic theme switching
const GlobalStyles = createGlobalStyle`
  :root {
    --body: ${theme('theme', {
      light: 'var(--body-light)',
      dark: 'var(--body-dark)',
    })};
    --widget: ${theme('theme', {
      light: 'var(--widget-light)',
      dark: 'var(--widget-dark)',
    })};
    --widget-shadow: ${theme('theme', {
      light: '0 1px 8px rgba(110, 110, 110, .15)',
      dark: '0 1px 8px rgba(33, 33, 33, .15)',
    })};
    --winget-inner-shadow: ${theme('theme', {
      light: 'linear-gradient(270deg, rgba(255, 255, 255, 0.0001) 0%, #FFFFFF 100%)',
      dark: 'linear-gradient(270deg, rgba(17, 19, 18, 0.0001) 0%, #111312 100%)',
    })};
    --text: ${theme('theme', {
      light: 'var(--text-light)',
      dark: 'var(--text-dark)',
    })};
    --border: ${theme('theme', {
      light: 'var(--border-light)',
      dark: 'var(--border-dark)',
    })};
    --header: ${theme('theme', {
      light: 'var(--header-light)',
      dark: 'var(--header-dark)',
    })};
    --header-invert: ${theme('theme', {
      light: 'var(--header-dark)',
      dark: 'var(--header-light)',
    })};
    --label: ${theme('theme', {
      light: 'var(--btn-text-light)',
      dark: 'var(--text-dark)',
    })};
    --btn-text: ${theme('theme', {
      light: 'var(--btn-text-light)',
      dark: 'var(--widget-light)',
    })};
    --highlight: ${theme('theme', {
      light: 'var(--olive)',
      dark: 'var(--accent)',
    })};
    --highlight-invert: ${theme('theme', {
      light: 'var(--accent)',
      dark: 'var(--olive)',
    })};
    --tooltip-bg: ${theme('theme', {
      light: 'var(--widget)',
      dark: 'var(--border)',
    })};
    --nav-arrow-color: ${theme('theme', {
      light: 'var(--header)',
      dark: '#8EA2AB',
    })};
    --nav-arrow-hover-color: ${theme('theme', {
      light: 'var(--blue)',
      dark: 'var(--accent)',
    })};
    --divider: ${theme('theme', {
      light: '#ccc',
      dark: '#3e3e3e',
    })};
    --placeholder: ${theme('theme', {
      light: '#8C9876',
      dark: '#999999'
    })};
    --score-alt: ${theme('theme', {
      light: 'var(--border)',
      dark: 'var(--black-3)'
    })};
    --bg-card: ${theme('theme', {
      light: 'var(--widget-light)',
      dark: 'var(--widget-dark)'
    })};
    --highlight-row: ${theme('theme', {
      light: 'rgba(253, 202, 64, 0.1)',
      dark: 'rgba(253, 202, 64, 0.1)'
    })};
    --shadow-sm: ${theme('theme', {
      light: 'rgba(110, 110, 110, 0.1)',
      dark: 'rgba(33, 33, 33, 0.1)'
    })};
    --shadow-md: ${theme('theme', {
      light: 'rgba(110, 110, 110, 0.2)',
      dark: 'rgba(33, 33, 33, 0.2)'
    })};
    /* Hairline divider for use on card surfaces. Independently tuned per theme
       because the dark-theme card background is var(--border) — any divider
       drawn in var(--border) becomes invisible. */
    --hairline: ${theme('theme', {
      light: 'color-mix(in srgb, var(--header) 8%, transparent)',
      dark: 'color-mix(in srgb, var(--header) 14%, transparent)'
    })};
    /* Faint tinted surface used for the identity band on the participant
       statistics page (and any future "subtle inset card" pattern). Resolves
       to a near-widget surface with a slight greenish wash in light, and a
       slightly lifted near-widget surface in dark. */
    --tint: ${theme('theme', {
      light: 'color-mix(in srgb, var(--header) 4%, var(--widget))',
      dark: 'color-mix(in srgb, var(--header) 6%, var(--widget))'
    })};
    /* Darker accent variant for the "exact hit" KPI value on the
       participant statistics page. In dark theme collapses back to --accent
       because the standard yellow already contrasts well against the
       dark widget surface. */
    --accent-strong: ${theme('theme', {
      light: '#caa01f',
      dark: 'var(--accent)'
    })};
    /* Neutral "pending" segment in the prediction distribution bar.
       Independently tuned per theme because both --border and --text are
       already used elsewhere — this surface needs a third value that reads
       as "muted but distinct" against the widget background. */
    --neutral-seg: ${theme('theme', {
      light: '#C9CEBE',
      dark: '#3a4038'
    })};
  }
`;

export default GlobalStyles;
